// src/components/common/DataTable.js
import React, { useMemo, useRef, useState } from "react";
import utils from "../../utils/utils";
import DropdownMenu from "./DropDownMenu/dropdownmenu";
import "./DataTable.scss";

const DataTableRowActionsMenu = ({ row, actions }) => {
  const [showMenu, setShowMenu] = useState(false);
  const triggerRef = useRef(null);

  const items = useMemo(() => {
    const visibleActions = actions.filter((action) => {
      if (typeof action.visible === "function" && !action.visible(row)) {
        return false;
      }
      if (action.disabled && action.disabled(row)) {
        return false;
      }
      return true;
    });

    const menuItems = [];
    visibleActions.forEach((action) => {
      if (action.dividerBefore && menuItems.length > 0) {
        menuItems.push({ divider: true });
      }
      menuItems.push({
        label:
          typeof action.label === "function" ? action.label(row) : action.label,
        icon:
          typeof action.icon === "function" ? action.icon(row) : action.icon,
        onClick: () => action.onClick(row),
      });
    });
    return menuItems;
  }, [actions, row]);

  if (items.length === 0) return null;

  return (
    <div className="position-relative d-inline-block datatable-row-actions">
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-sm btn-outline-secondary datatable-row-actions-trigger"
        onClick={() => setShowMenu((prev) => !prev)}
        aria-label="Row actions"
        title="Actions"
      >
        <i className="fas fa-ellipsis-v" aria-hidden="true" />
      </button>
      <DropdownMenu
        items={items}
        show={showMenu}
        onClose={() => setShowMenu(false)}
        trigger={triggerRef}
        align="end"
      />
    </div>
  );
};

const renderActionButtons = (row, actions) =>
  actions.map((action, i) => {
    if (typeof action.visible === "function" && !action.visible(row)) {
      return null;
    }
    const label =
      typeof action.label === "function" ? action.label(row) : action.label;
    const className =
      typeof action.className === "function"
        ? action.className(row)
        : action.className || "btn-primary";
    const title =
      typeof action.title === "function" ? action.title(row) : action.title;
    return (
      <button
        key={i}
        className={`btn btn-sm me-2 ${className}`}
        onClick={() => action.onClick(row)}
        disabled={action.disabled && action.disabled(row)}
        title={title || undefined}
      >
        {label}
      </button>
    );
  });

const DataTable = ({
  data,
  columns,
  actions = [],
  sortable = [],
  showToggle = false,
  actionsColumnLabel = "Actions",
  actionsAsMenu = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rowsPerPageOption, setRowsPerPageOption] = useState("10");
  const [viewMode, setViewMode] = useState("table"); // table | grid

  const cellPlainTextTitle = (value) => {
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    return undefined;
  };

  const getColumnAlignment = (col) => {
    const align = String(col?.align || "left").toLowerCase();
    if (align === "center" || align === "right") return align;
    return "left";
  };

  // Function to render cell content based on column type
  const renderCellContent = (row, col) => {
    if (typeof col.render === "function") {
      return col.render(row);
    }
    // Handle image type
    if (col.type === "image") {
      const imageUrl = row[col.accessor];
      return (
        <img
          src={
            imageUrl ||
            col.defaultImage ||
            "https://via.placeholder.com/40x40?text=No+Image"
          }
          alt={col.label}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              col.defaultImage ||
              "https://via.placeholder.com/40x40?text=No+Image";
          }}
        />
      );
    }

    // Handle concatenated values
    if (col.type === "concat") {
      const fields = col.accessor.split(",");
      const values = fields
        .map((field) => row[field.trim()])
        .filter((val) => val);
      return values.join(col.separator || " ");
    }
    if (col.type === "bool") {
      const value = row[col.accessor.trim()];
      return value ? "Yes" : "No";
    }

    if (col.type === "badge") {
      const value = row[col.accessor];
      const label = value || "Unknown";
      const key = String(label).toLowerCase();
      const colorMap = col.badgeColors || {};
      const bgColor = colorMap[key] || "#6c757d";
      return (
        <span
          className="badge"
          style={{
            backgroundColor: bgColor,
            color: "#fff",
            fontSize: "0.78rem",
            fontWeight: 500,
            padding: "0.3em 0.65em",
            borderRadius: "0.35rem",
            textTransform: "capitalize",
          }}
        >
          {label}
        </span>
      );
    }

    if (col.colorAccessor && row[col.colorAccessor]) {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span
            style={{
              backgroundColor: row[col.colorAccessor],
              height: "14px",
              width: "14px",
              display: "inline-block",
              borderRadius: "50px",
              border: "1px solid #ccc",
            }}
          ></span>
          {row[col.accessor]}
        </span>
      );
    }

    if (col.type === "CireclePost") {
      const count = row[col.cireclePostAccessor];
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span
            className="CireclePost"
            title={count != null ? String(count) : ""}
            style={{
              backgroundColor: "#604BBA",
              minWidth: "22px",
              height: "22px",
              padding: "0 6px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "999px",
              border: "1px solid rgba(0,0,0,0.08)",
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {count != null && count !== "" ? count : "—"}
          </span>
          <span>{row[col.accessor]}</span>
        </span>
      );
    }

    /** DD/MM/YYYY hh:mm AM/PM — see utils.formatDateTimeDisplay */
    if (col.type === "date") {
      return utils.formatDateTimeDisplay(row[col.accessor]);
    }

    return row[col.accessor];
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (!sortable.includes(column)) return;
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === column &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key: column, direction });
  };

  /** Sort key for date columns — aligned with utils.parseDateInput / display format */
  const parseDateValue = (val) => {
    if (val === "-") return null;
    const d = utils.parseDateInput(val);
    return d == null ? null : d.getTime();
  };

  const sortedRows = useMemo(() => {
    const filteredData = data.filter((row) =>
      columns.some((col) =>
        String(row[col.accessor]).toLowerCase().includes(searchTerm),
      ),
    );

    if (sortConfig) {
      const colDef = columns.find((c) => c.accessor === sortConfig.key);
      const isDateColumn = colDef?.type === "date";

      filteredData.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];

        // Push nulls/undefined to the end regardless of direction
        if (valA == null && valB == null) return 0;
        if (valA == null) return 1;
        if (valB == null) return -1;

        if (isDateColumn) {
          const tsA = parseDateValue(valA);
          const tsB = parseDateValue(valB);
          if (tsA == null && tsB == null) return 0;
          if (tsA == null) return 1;
          if (tsB == null) return -1;
          const diff = tsA - tsB;
          return sortConfig.direction === "ascending" ? diff : -diff;
        }

        if (valA < valB) return sortConfig.direction === "ascending" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }

    return filteredData;
  }, [columns, data, searchTerm, sortConfig]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [currentPage, rowsPerPage, sortedRows]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    setRowsPerPageOption(value);
    if (value === "All") {
      setRowsPerPage(Math.max(1, sortedRows.length));
    } else {
      setRowsPerPage(parseInt(value, 10));
    }
    setCurrentPage(1);
  };

  return (
    <div className="datatable-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search..."
          onChange={handleSearch}
          style={{ height: "2.3rem" }}
        />

        <div className="d-flex align-items-center">
          {showToggle && (
            <button
              className="btn btn-outline-secondary btn-sm me-3"
              onClick={() =>
                setViewMode(viewMode === "table" ? "grid" : "table")
              }
              style={{
                width: "6rem",
                height: "2.3rem",
                borderRadius: "0.5rem",
                border: "solid 1px ",
                borderColor: "#ddd",
              }}
            >
              {viewMode === "table" ? (
                <>
                  <i className="fa fa-th-large me-1"></i> Grid View
                </>
              ) : (
                <>
                  <i className="fa fa-table me-1"></i> Table View
                </>
              )}
            </button>
          )}
          <span className="me-2">Rows:</span>
          <select
            style={{ width: "5rem", height: "2.3rem" }}
            className="form-select"
            value={rowsPerPageOption}
            onChange={handleRowsPerPageChange}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="All">All</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        {viewMode === "table" ? (
          <table className="table table-bordered table-hover">
            <thead className="datatable-header">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.accessor}
                    onClick={() =>
                      sortable.includes(col.accessor) &&
                      handleSort(col.accessor)
                    }
                    className={
                      sortable.includes(col.accessor) ? "sortable" : ""
                    }
                    style={{
                      textAlign: getColumnAlignment(col),
                      ...(col.width ? { width: col.width } : {}),
                    }}
                  >
                    {col.label}{" "}
                    {sortable.includes(col.accessor) &&
                      sortConfig?.key !== col.accessor && (
                        <span className="sort-icons">↑↓</span>
                      )}
                    {sortable.includes(col.accessor) &&
                      sortConfig?.key === col.accessor && (
                        <span className="sort-icons">
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                  </th>
                ))}
                {actions && Array.isArray(actions) && actions.length > 0 && (
                  <th>{actionsColumnLabel}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (actions && Array.isArray(actions) && actions.length > 0 ? 1 : 0)
                    }
                    className="text-center text-muted py-4"
                  >
                    No data found.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((col) => {
                    const cell = renderCellContent(row, col);
                    return (
                      <td
                        key={col.accessor}
                        title={cellPlainTextTitle(cell)}
                        style={{
                          textAlign: getColumnAlignment(col),
                          ...(col.width ? { width: col.width } : {}),
                        }}
                      >
                        {cell}
                      </td>
                    );
                  })}
                  {actions.length > 0 && (
                    <td className="datatable-actions-cell">
                      {actionsAsMenu ? (
                        <DataTableRowActionsMenu row={row} actions={actions} />
                      ) : (
                        Array.isArray(actions) && renderActionButtons(row, actions)
                      )}
                    </td>
                  )}
                </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <div className="row">
            {paginatedRows.length === 0 ? (
              <div className="col-12 text-center text-muted py-4">No data found.</div>
            ) : (
              paginatedRows.map((row, index) => (
                <div key={index} className="col-md-3 mb-3">
                  <div className="card  shadow-sm">
                    <div className="card-body">
                      {columns.map((col) => {
                        const cell = renderCellContent(row, col);
                        return (
                          <p
                            key={col.accessor}
                            className="mb-1"
                            style={{ textAlign: getColumnAlignment(col) }}
                          >
                            <strong title={col.label}>{col.label}:</strong>{" "}
                            <span title={cellPlainTextTitle(cell)}>{cell}</span>
                          </p>
                        );
                      })}
                      {actions.length > 0 && (
                        <div className="mt-2">
                          {actionsAsMenu ? (
                            <DataTableRowActionsMenu row={row} actions={actions} />
                          ) : (
                            renderActionButtons(row, actions)
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="page-info">
          Showing {paginatedRows.length} of {sortedRows.length} rows
        </span>

        <div>
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          <span>{currentPage}</span>
          <button
            className="btn btn-sm btn-outline-secondary ms-2"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
